import type { RfqPackage, RfqProject, RfqProjectStatus, RfqSubmission } from '../types';

/** Synthetic release id for the pre-portal version row in RFQ version trees. */
export const PRE_PORTAL_RELEASE_ID = 'pre_portal';

/** @deprecated Firestore rejects map keys surrounded by `__`; read-only alias for legacy docs. */
export const LEGACY_PRE_PORTAL_RELEASE_ID = '__pre_portal__';

export function isPrePortalReleaseId(releaseId: string | null | undefined): boolean {
  return releaseId === PRE_PORTAL_RELEASE_ID || releaseId === LEGACY_PRE_PORTAL_RELEASE_ID;
}

function normalizePrePortalReleaseMap<T>(
  map: Record<string, T> | undefined,
): Record<string, T> {
  if (!map) return {};
  const out = { ...map };
  const legacy = out[LEGACY_PRE_PORTAL_RELEASE_ID];
  const current = out[PRE_PORTAL_RELEASE_ID];
  if (legacy !== undefined) {
    out[PRE_PORTAL_RELEASE_ID] = (current ?? legacy) as T;
    delete out[LEGACY_PRE_PORTAL_RELEASE_ID];
  }
  return out;
}

function releaseScopedIds(
  map: Record<string, string[]> | undefined,
  releaseId: string,
): string[] {
  const normalized = normalizePrePortalReleaseMap(map);
  if (isPrePortalReleaseId(releaseId)) {
    return normalized[PRE_PORTAL_RELEASE_ID] ?? [];
  }
  return normalized[releaseId] ?? [];
}

/** Strip legacy `__pre_portal__` map keys before Firestore writes (invalid field names). */
export function sanitizeRfqProjectFirestoreUpdates(
  updates: Partial<RfqProject>,
): Partial<RfqProject> {
  const out = { ...updates };
  if (out.approvedSubmissionIdsByRelease) {
    out.approvedSubmissionIdsByRelease = normalizePrePortalReleaseMap(
      out.approvedSubmissionIdsByRelease,
    );
  }
  if (out.approvedSupplierOrgIdsByRelease) {
    out.approvedSupplierOrgIdsByRelease = normalizePrePortalReleaseMap(
      out.approvedSupplierOrgIdsByRelease,
    );
  }
  if (out.approvedSupplierOrgIdByRelease) {
    out.approvedSupplierOrgIdByRelease = normalizePrePortalReleaseMap(
      out.approvedSupplierOrgIdByRelease,
    );
  }
  if (out.approvedSupplierApproverInitialsByRelease) {
    out.approvedSupplierApproverInitialsByRelease = normalizePrePortalReleaseMap(
      out.approvedSupplierApproverInitialsByRelease,
    );
  }
  if (out.approvedSupplierApprovedAtByRelease) {
    out.approvedSupplierApprovedAtByRelease = normalizePrePortalReleaseMap(
      out.approvedSupplierApprovedAtByRelease,
    );
  }
  return out;
}

/** @deprecated Use {@link PRE_PORTAL_RELEASE_ID}. */
export const PRE_PORTAL_TIMBER_RELEASE_ID = PRE_PORTAL_RELEASE_ID;

/** @deprecated Use {@link PRE_PORTAL_RELEASE_ID}. */
export const PRE_PORTAL_CNC_RELEASE_ID = PRE_PORTAL_RELEASE_ID;

/** Reserved version index for staff legacy imports — staged hub releases start at v1. */
export const PRE_PORTAL_RELEASE_VERSION_INDEX = 0;

export type RfqPackageReleaseFields = Pick<
  RfqPackage,
  'legacyImport' | 'quotePlaceholder' | 'releaseVersionIndex' | 'hubFileReleaseId'
>;

/**
 * True for synthetic v0 packages without a hub release: legacy imports and
 * CNC quote placeholders created from a combined timber+CNC quote split.
 * Both reuse {@link PRE_PORTAL_RELEASE_ID} for approval maps.
 */
export function isPrePortalPackage(pkg: RfqPackageReleaseFields): boolean {
  if (pkg.legacyImport) return true;
  if (pkg.quotePlaceholder) return true;
  return (
    typeof pkg.releaseVersionIndex === 'number'
    && pkg.releaseVersionIndex === PRE_PORTAL_RELEASE_VERSION_INDEX
    && !pkg.hubFileReleaseId
  );
}

/** CNC package-only row from a combined-quote split (not a legacy import). */
export function isQuotePlaceholderPackage(pkg: RfqPackageReleaseFields): boolean {
  return pkg.quotePlaceholder === true;
}

/** Shown when portal actions are unavailable on imported pre-portal packages. */
export const PRE_PORTAL_VENDOR_PORTAL_NO_FILES_HINT =
  'Pre-portal quote — LC Timber Frames imported this bid before package files were published in the supplier portal, so there is no timber list, PDF, or package detail to download.';

/** @deprecated Use {@link isPrePortalPackage}. */
export const isPrePortalTimberPackage = isPrePortalPackage;

export function hasPrePortalPackages(packages: RfqPackage[]): boolean {
  return packages.some(isPrePortalPackage);
}

/** @deprecated Use {@link hasPrePortalPackages}. */
export const hasPrePortalTimberPackages = hasPrePortalPackages;

/** @deprecated Use {@link hasPrePortalPackages}. */
export const hasPrePortalCncPackages = hasPrePortalPackages;

export function filterPackagesForReleaseSelection(
  packages: RfqPackage[],
  selectedReleaseId: string | null,
): RfqPackage[] {
  if (!selectedReleaseId) return packages;
  if (isPrePortalReleaseId(selectedReleaseId)) {
    return packages.filter(isPrePortalPackage);
  }
  return packages.filter(p => p.hubFileReleaseId === selectedReleaseId);
}

/** @deprecated Use {@link filterPackagesForReleaseSelection}. */
export const filterTimberPackagesForReleaseSelection = filterPackagesForReleaseSelection;

/** @deprecated Use {@link filterPackagesForReleaseSelection}. */
export const filterCncPackagesForReleaseSelection = filterPackagesForReleaseSelection;

export function releaseVersionLabel(
  releaseVersionIndex: number,
  options?: { quotePlaceholder?: boolean },
): string {
  if (releaseVersionIndex === PRE_PORTAL_RELEASE_VERSION_INDEX) {
    if (options?.quotePlaceholder) return 'v0';
    return 'v0 (Pre-portal)';
  }
  return `v${releaseVersionIndex}`;
}

/** @deprecated Use {@link releaseVersionLabel}. */
export const timberReleaseVersionLabel = releaseVersionLabel;

/** @deprecated Use {@link releaseVersionLabel}. */
export const cncReleaseVersionLabel = releaseVersionLabel;

function isActiveQuoteSubmission(sub: RfqSubmission): boolean {
  return !sub.notBidding && (!!sub.storagePath || !!sub.cncQuote);
}

export function countActiveQuotesForReleaseSelection(
  packages: RfqPackage[],
  submissionsByPackageId: Map<string, RfqSubmission[]>,
  selectedReleaseId: string | null,
): number {
  const filtered = filterPackagesForReleaseSelection(packages, selectedReleaseId);
  let count = 0;
  for (const pkg of filtered) {
    if (!pkg.id) continue;
    const subs = submissionsByPackageId.get(pkg.id) ?? [];
    count += subs.filter(isActiveQuoteSubmission).length;
  }
  return count;
}

/** Status for the section header when a specific release version is selected. */
export function displayStatusForReleaseSelection(
  projectStatus: RfqProjectStatus | undefined,
  packages: RfqPackage[],
  submissionsByPackageId: Map<string, RfqSubmission[]>,
  selectedReleaseId: string | null,
): RfqProjectStatus | undefined {
  if (!selectedReleaseId) return projectStatus;
  const quoteCount = countActiveQuotesForReleaseSelection(
    packages,
    submissionsByPackageId,
    selectedReleaseId,
  );
  if (quoteCount > 0) return 'quoted';
  const hasPublishedPackage = filterPackagesForReleaseSelection(packages, selectedReleaseId).length > 0;
  if (hasPublishedPackage) return 'active';
  return 'active';
}

/** Release key for storing version-scoped quote approvals. */
export function releaseKeyForPackage(pkg: RfqPackageReleaseFields): string {
  if (isPrePortalPackage(pkg)) return PRE_PORTAL_RELEASE_ID;
  return pkg.hubFileReleaseId || PRE_PORTAL_RELEASE_ID;
}

export function submissionIdsForReleaseSelection(
  packages: RfqPackage[],
  submissionsByPackageId: Map<string, RfqSubmission[]>,
  selectedReleaseId: string | null,
): Set<string> {
  const ids = new Set<string>();
  for (const pkg of filterPackagesForReleaseSelection(packages, selectedReleaseId)) {
    if (!pkg.id) continue;
    for (const sub of submissionsByPackageId.get(pkg.id) ?? []) {
      if (sub.id) ids.add(sub.id);
    }
  }
  return ids;
}

/** Approved timber submission ids for the selected release only. */
export function approvedSubmissionIdsForReleaseSelection(
  project: Pick<RfqProject, 'approvedSubmissionIds' | 'approvedSubmissionIdsByRelease'>,
  packages: RfqPackage[],
  submissionsByPackageId: Map<string, RfqSubmission[]>,
  selectedReleaseId: string | null,
): string[] {
  if (!selectedReleaseId) return project.approvedSubmissionIds ?? [];
  const scoped = releaseScopedIds(project.approvedSubmissionIdsByRelease, selectedReleaseId);
  if (scoped.length) return scoped;
  const releaseSubmissionIds = submissionIdsForReleaseSelection(
    packages,
    submissionsByPackageId,
    selectedReleaseId,
  );
  return (project.approvedSubmissionIds ?? []).filter(id => releaseSubmissionIds.has(id));
}

export function isSubmissionApprovedForRelease(
  project: Pick<RfqProject, 'approvedSubmissionIds' | 'approvedSubmissionIdsByRelease'>,
  packages: RfqPackage[],
  submissionsByPackageId: Map<string, RfqSubmission[]>,
  selectedReleaseId: string | null,
  submissionId: string,
): boolean {
  return approvedSubmissionIdsForReleaseSelection(
    project,
    packages,
    submissionsByPackageId,
    selectedReleaseId,
  ).includes(submissionId);
}

export function buildTimberApprovalUpdate(
  project: Pick<
    RfqProject,
    | 'approvedSubmissionIds'
    | 'approvedSubmissionIdsByRelease'
    | 'approvedSubmissionApproverInitials'
    | 'approvedSubmissionApprovedAt'
  >,
  releaseId: string,
  submissionId: string,
  approve: boolean,
  approverInitials?: string,
  options?: { clearSubmissionIds?: string[]; approvedAt?: Date },
): Partial<RfqProject> {
  const byRelease = normalizePrePortalReleaseMap(project.approvedSubmissionIdsByRelease ?? {});
  const storageReleaseId = isPrePortalReleaseId(releaseId) ? PRE_PORTAL_RELEASE_ID : releaseId;
  const bucket = new Set(releaseScopedIds(byRelease, releaseId));
  if (approve) bucket.add(submissionId);
  else bucket.delete(submissionId);
  if (approve) {
    for (const id of options?.clearSubmissionIds ?? []) {
      if (id && id !== submissionId) bucket.delete(id);
    }
  }
  const nextBucket = Array.from(bucket);
  if (nextBucket.length) byRelease[storageReleaseId] = nextBucket;
  else delete byRelease[storageReleaseId];

  const global = new Set(project.approvedSubmissionIds ?? []);
  if (approve) global.add(submissionId);
  else global.delete(submissionId);
  if (approve) {
    for (const id of options?.clearSubmissionIds ?? []) {
      if (id && id !== submissionId) global.delete(id);
    }
  }

  const approvers = { ...(project.approvedSubmissionApproverInitials ?? {}) };
  if (approve && approverInitials?.trim()) {
    approvers[submissionId] = approverInitials.trim();
  } else if (!approve) {
    delete approvers[submissionId];
  }
  if (approve) {
    for (const id of options?.clearSubmissionIds ?? []) {
      if (id && id !== submissionId) delete approvers[id];
    }
  }

  const approvedAt = { ...(project.approvedSubmissionApprovedAt ?? {}) };
  if (approve) {
    approvedAt[submissionId] = options?.approvedAt ?? new Date();
    for (const id of options?.clearSubmissionIds ?? []) {
      if (id && id !== submissionId) delete approvedAt[id];
    }
  } else {
    delete approvedAt[submissionId];
  }

  // Always write arrays/maps (possibly empty): `undefined` is stripped before the
  // Firestore update, which would leave the last award stuck on the project.
  return {
    approvedSubmissionIdsByRelease: byRelease,
    approvedSubmissionIds: Array.from(global),
    approvedSubmissionApproverInitials: approvers,
    approvedSubmissionApprovedAt: approvedAt,
  };
}

/** Other quotes use the same per-submission approval model as timber RFQ. */
export const buildOtherQuoteApprovalUpdate = buildTimberApprovalUpdate;

type CncApprovalFields = Pick<
  RfqProject,
  | 'approvedSupplierOrgId'
  | 'approvedSupplierOrgIdByRelease'
  | 'approvedSupplierOrgIdsByRelease'
  | 'approvedSupplierApproverInitialsByRelease'
  | 'approvedSupplierApprovedAtByRelease'
>;

/**
 * CNC supplier approvals for the selected release. Multiple suppliers can be
 * awarded one release when structure-split quotes each cover part of the order.
 * Reads the multi-award map first and unions the legacy single-award fields.
 */
export function approvedSupplierOrgIdsForReleaseSelection(
  project: CncApprovalFields,
  selectedReleaseId: string | null,
): string[] {
  if (!selectedReleaseId) {
    return project.approvedSupplierOrgId ? [project.approvedSupplierOrgId] : [];
  }
  const normalizedMulti = normalizePrePortalReleaseMap(project.approvedSupplierOrgIdsByRelease ?? {});
  const normalizedLegacy = normalizePrePortalReleaseMap(project.approvedSupplierOrgIdByRelease ?? {});
  const storageReleaseId = isPrePortalReleaseId(selectedReleaseId)
    ? PRE_PORTAL_RELEASE_ID
    : selectedReleaseId;
  const out: string[] = [];
  for (const id of normalizedMulti[storageReleaseId] ?? []) {
    if (id && !out.includes(id)) out.push(id);
  }
  const legacy = normalizedLegacy[storageReleaseId];
  if (legacy && !out.includes(legacy)) out.push(legacy);
  if (!out.length && isPrePortalReleaseId(selectedReleaseId) && project.approvedSupplierOrgId) {
    out.push(project.approvedSupplierOrgId);
  }
  return out;
}

/** @deprecated Use {@link approvedSupplierOrgIdsForReleaseSelection} — CNC releases support multiple awards. */
export function approvedSupplierOrgIdForReleaseSelection(
  project: CncApprovalFields,
  selectedReleaseId: string | null,
): string | undefined {
  return approvedSupplierOrgIdsForReleaseSelection(project, selectedReleaseId)[0];
}

export function buildCncSupplierApprovalUpdate(
  project: CncApprovalFields,
  releaseId: string,
  orgId: string,
  approve: boolean,
  approverInitials?: string,
  options?: { approvedAt?: Date },
): Partial<RfqProject> {
  const byReleaseMulti = normalizePrePortalReleaseMap(project.approvedSupplierOrgIdsByRelease ?? {});
  const storageReleaseId = isPrePortalReleaseId(releaseId) ? PRE_PORTAL_RELEASE_ID : releaseId;
  const bucket = byReleaseMulti[storageReleaseId] ? [...byReleaseMulti[storageReleaseId]] : [];
  const byReleaseLegacyMap = normalizePrePortalReleaseMap(project.approvedSupplierOrgIdByRelease ?? {});
  const legacyEntry = byReleaseLegacyMap[storageReleaseId];
  if (legacyEntry && !bucket.includes(legacyEntry)) bucket.push(legacyEntry);

  const nextBucket = approve
    ? bucket.includes(orgId)
      ? bucket
      : [...bucket, orgId]
    : bucket.filter(id => id !== orgId);
  if (nextBucket.length) byReleaseMulti[storageReleaseId] = nextBucket;
  else delete byReleaseMulti[storageReleaseId];

  // Mirror the first award into the legacy single-award map for older readers.
  const byReleaseLegacy = { ...byReleaseLegacyMap };
  if (nextBucket.length) byReleaseLegacy[storageReleaseId] = nextBucket[0];
  else delete byReleaseLegacy[storageReleaseId];

  const approverByRelease = normalizePrePortalReleaseMap(
    project.approvedSupplierApproverInitialsByRelease ?? {},
  );
  const releaseApprovers = { ...(approverByRelease[storageReleaseId] ?? {}) };
  if (approve && approverInitials?.trim()) {
    releaseApprovers[orgId] = approverInitials.trim();
  } else if (!approve) {
    delete releaseApprovers[orgId];
  }
  if (Object.keys(releaseApprovers).length) approverByRelease[storageReleaseId] = releaseApprovers;
  else delete approverByRelease[storageReleaseId];

  const approvedAtByRelease = normalizePrePortalReleaseMap(project.approvedSupplierApprovedAtByRelease ?? {});
  const releaseDates = { ...(approvedAtByRelease[storageReleaseId] ?? {}) };
  if (approve) {
    releaseDates[orgId] = options?.approvedAt ?? new Date();
  } else {
    delete releaseDates[orgId];
  }
  if (Object.keys(releaseDates).length) approvedAtByRelease[storageReleaseId] = releaseDates;
  else delete approvedAtByRelease[storageReleaseId];

  // Always write the maps (possibly empty objects): `undefined` values are
  // stripped before the Firestore update, which would leave stale awards behind.
  const updates: Partial<RfqProject> = {
    approvedSupplierOrgIdsByRelease: byReleaseMulti,
    approvedSupplierOrgIdByRelease: byReleaseLegacy,
    approvedSupplierApproverInitialsByRelease: approverByRelease,
    approvedSupplierApprovedAtByRelease: approvedAtByRelease,
  };

  if (isPrePortalReleaseId(releaseId)) {
    const nextGlobal = approve
      ? orgId
      : project.approvedSupplierOrgId === orgId
        ? nextBucket[0] ?? null
        : project.approvedSupplierOrgId ?? null;
    // null (not undefined) so clearing survives the undefined-stripping update path.
    (updates as Record<string, unknown>).approvedSupplierOrgId = nextGlobal;
  }

  return updates;
}

type AwardReleaseProject = Pick<
  RfqProject,
  | 'approvedSubmissionIds'
  | 'approvedSubmissionIdsByRelease'
  | 'approvedSupplierOrgId'
  | 'approvedSupplierOrgIdByRelease'
  | 'approvedSupplierOrgIdsByRelease'
>;

/** True when the selected release still has an approved timber or CNC quote. */
export function releaseHasAwardForSelection(
  project: AwardReleaseProject,
  packages: RfqPackage[],
  submissionsByPackageId: Map<string, RfqSubmission[]>,
  selectedReleaseId: string | null,
  workType: 'timber_rfq' | 'cnc_quote' | 'other_quote',
): boolean {
  if (!selectedReleaseId) return false;
  if (workType === 'cnc_quote') {
    return approvedSupplierOrgIdsForReleaseSelection(project, selectedReleaseId).length > 0;
  }
  return (
    approvedSubmissionIdsForReleaseSelection(
      project,
      packages,
      submissionsByPackageId,
      selectedReleaseId,
    ).length > 0
  );
}

/**
 * Release ids (staged + pre-portal) that currently carry an approved quote.
 * Order matches {@link stagedReleaseIds} (newest first), then pre-portal last.
 */
export function releaseIdsWithAwards(
  project: AwardReleaseProject,
  packages: RfqPackage[],
  submissionsByPackageId: Map<string, RfqSubmission[]>,
  stagedReleaseIds: string[],
  workType: 'timber_rfq' | 'cnc_quote' | 'other_quote',
): string[] {
  const out: string[] = [];
  for (const id of stagedReleaseIds) {
    if (
      id
      && releaseHasAwardForSelection(project, packages, submissionsByPackageId, id, workType)
    ) {
      out.push(id);
    }
  }
  if (
    hasPrePortalPackages(packages)
    && releaseHasAwardForSelection(
      project,
      packages,
      submissionsByPackageId,
      PRE_PORTAL_RELEASE_ID,
      workType,
    )
  ) {
    out.push(PRE_PORTAL_RELEASE_ID);
  }
  return out;
}

export type ResolveDefaultRfqReleaseIdInput = {
  prev: string;
  /** Staged hub releases, newest first. */
  stagedReleaseIds: string[];
  packages: RfqPackage[];
  submissionsByPackageId: Map<string, RfqSubmission[]>;
  project: AwardReleaseProject;
  workType: 'timber_rfq' | 'cnc_quote' | 'other_quote';
};

/**
 * Default Suppliers-tab release selection.
 *
 * Prefer the latest staged release when it already has quotes or awards.
 * When Latest is empty but an older release (or pre-portal) still has approved
 * quotes, land on that award release so staff do not think awards vanished.
 */
export function resolveDefaultRfqReleaseId(input: ResolveDefaultRfqReleaseIdInput): string {
  const {
    prev,
    stagedReleaseIds,
    packages,
    submissionsByPackageId,
    project,
    workType,
  } = input;
  const latestStagedId = stagedReleaseIds[0];

  if (prev && stagedReleaseIds.includes(prev)) return prev;

  const awardIds = releaseIdsWithAwards(
    project,
    packages,
    submissionsByPackageId,
    stagedReleaseIds,
    workType,
  );

  if (latestStagedId) {
    const latestHasQuotes =
      countActiveQuotesForReleaseSelection(
        packages,
        submissionsByPackageId,
        latestStagedId,
      ) > 0;
    const latestHasAward = awardIds.includes(latestStagedId);
    if (latestHasQuotes || latestHasAward) return latestStagedId;
    if (awardIds.length) return awardIds[0]!;
    return latestStagedId;
  }

  if (hasPrePortalPackages(packages)) return PRE_PORTAL_RELEASE_ID;
  return '';
}
